import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, ChatBubbleOutline, TableChart, ViewList } from "@mui/icons-material";
import dayjs, { Dayjs } from "dayjs";
import { useAuth } from "@/context/AuthContext";
import { createFlashService } from "@/api/flash.service";
import { HierarchyPanel } from "@/components/panel/HierarchyPanel";
import { FlashComparativo } from "@/components/panel/comparativo/FlashComparativo";
import { ChatDrawer } from "@/components/panel/chat/ChatDrawer";
import { CrudPage } from "@/components/crud/CrudPage";

const FLASH_CONFIGS: Record<string, { title: string; dateParam: string; hierarchyQueryId: string }> = {
  "flash-de-vendas": { title: "Flash de Vendas", dateParam: "data_venda", hierarchyQueryId: "flash" },
  "flash-de-contribuicao": { title: "Flash de Contribuição", dateParam: "data_contribuicao", hierarchyQueryId: "flashContribuicao" },
  "flash-de-perdas": { title: "Flash de Perdas", dateParam: "data_perda", hierarchyQueryId: "flashPerdas" },
};

type ViewMode = "hierarquia" | "comparativo";

// ponytail: allowlist temporária espelhando app-mobile/src/app/flashPerdas.tsx —
// remover quando a query flash_diario estiver disponível para todos os clientes.
const FLASH_DIARIO_CLIENTS = ["019e7021-c239-79de-830c-45a07b6e43cc", "019d68dd-fba0-7dae-896f-64f6ea0d8063"];

export function FlashPage() {
  const { moduleId = "" } = useParams<{ moduleId: string }>();
  const { user } = useAuth();
  const config = FLASH_CONFIGS[moduleId];

  // Data, visão e "DIA" ficam na URL, como o estado do HierarchyPanel: o "voltar" do navegador
  // (ex.: vindo da Consulta de Produtos pelo link do produto) reabre a tela como estava.
  const [params, setParams] = useSearchParams();
  const mudarUrl = (alterar: (p: URLSearchParams) => void) =>
    setParams(
      (atual) => {
        const prox = new URLSearchParams(atual);
        alterar(prox);
        return prox;
      },
      { replace: true },
    );

  const dataParam = params.get("data");
  const currentDate = useMemo(() => {
    const data = dataParam ? dayjs(dataParam) : null;
    return data?.isValid() && !data.isAfter(dayjs(), "day") ? data : dayjs();
  }, [dataParam]);
  // Hoje não vai para a URL: reaberta outro dia, a tela mostra o dia novo.
  const setCurrentDate = (data: Dayjs) =>
    mudarUrl((p) => {
      if (data.isSame(dayjs(), "day")) p.delete("data");
      else p.set("data", data.format("YYYY-MM-DD"));
    });
  const viewMode: ViewMode = params.get("visao") === "comparativo" ? "comparativo" : "hierarquia";
  const isDiario = params.get("dia") === "1";
  const [chatOpen, setChatOpen] = useState(false);
  const isFutureDay = !currentDate.isBefore(dayjs(), "day");
  const canUseFlashDiario = !!user?.loggedClient && FLASH_DIARIO_CLIENTS.includes(user.loggedClient.id);
  const effectiveIsDiario = isDiario && canUseFlashDiario;

  const flashService = useMemo(
    () => (config ? createFlashService({ moduleId, dateParam: config.dateParam, hierarchyQueryId: config.hierarchyQueryId }) : null),
    [moduleId, config],
  );

  if (!config || !flashService || !user?.loggedClient) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography color="text.secondary">Este módulo ainda não está disponível no desktop.</Typography>
      </Box>
    );
  }

  return (
    <CrudPage
      title={config.title}
      headerActions={
        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton size="small" onClick={() => setCurrentDate(currentDate.subtract(1, "day"))}>
            <ChevronLeft fontSize="small" />
          </IconButton>
          <TextField
            type="date"
            size="small"
            value={currentDate.format("YYYY-MM-DD")}
            onChange={(e) => e.target.value && setCurrentDate(dayjs(e.target.value))}
            slotProps={{ htmlInput: { max: dayjs().format("YYYY-MM-DD") } }}
          />
          <IconButton size="small" disabled={isFutureDay} onClick={() => setCurrentDate(currentDate.add(1, "day"))}>
            <ChevronRight fontSize="small" />
          </IconButton>
          {canUseFlashDiario && viewMode === "hierarquia" && (
            <Button
              size="small"
              variant={isDiario ? "contained" : "outlined"}
              onClick={() =>
                mudarUrl((p) => {
                  if (isDiario) p.delete("dia");
                  else p.set("dia", "1");
                })
              }
              sx={{ minWidth: 0, px: 1.5 }}
            >
              DIA
            </Button>
          )}
          <IconButton
            size="small"
            onClick={() =>
              mudarUrl((p) => {
                if (viewMode === "hierarquia") p.set("visao", "comparativo");
                else p.delete("visao");
              })
            }
            title={viewMode === "hierarquia" ? "Ver comparativo" : "Ver hierarquia"}
          >
            {viewMode === "hierarquia" ? <TableChart fontSize="small" /> : <ViewList fontSize="small" />}
          </IconButton>
          <IconButton size="small" onClick={() => setChatOpen(true)} title="Conversas">
            <ChatBubbleOutline fontSize="small" />
          </IconButton>
        </Stack>
      }
    >
      <ChatDrawer
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        clientId={user.loggedClient.id}
        userId={user.id}
        moduleId={moduleId}
      />
      {viewMode === "hierarquia" ? (
        <HierarchyPanel
          flashService={flashService}
          clientId={user.loggedClient.id}
          clientName={user.loggedClient.name}
          userId={user.id}
          currentDate={currentDate}
          isDiario={effectiveIsDiario}
        />
      ) : (
        <FlashComparativo
          flashService={flashService}
          clientId={user.loggedClient.id}
          userId={user.id}
          currentDate={currentDate}
        />
      )}
    </CrudPage>
  );
}
